import omit from 'lodash/omit';
import { updateSignUpFunnelLead } from '~/services';
import { getSignupFunnelLead } from '~/services/get-signup-funnel-lead';
import { store } from './store';
import { trackError } from './tracking';

type EventData = {
  status?: string;
  [key: string]: any;
};

const mergeEventData = (
  existing: Record<string, unknown>,
  eventData: EventData,
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...existing };

  Object.entries(eventData).forEach(([key, value]) => {
    if (Array.isArray(value) && Array.isArray(existing[key])) {
      merged[key] = [...(existing[key] as unknown[]), ...value];
    } else {
      merged[key] = value;
    }
  });

  return merged;
};

export enum EventName {
  UtilityScreen = 'utilityScreen',
  UserScreen = 'userScreen',
  EnrollmentScreen = 'enrollmentScreen',
  UtilityAccountsDetailsScreen = 'utilityAccountsDetailsScreen',
  MailingAddressScreen = 'mailingAddressScreen',
  SelfAttestationScreen = 'selfAttestationScreen',
  ProofDocUploadScreen = 'proofDocUploadScreen',
  LegalScreen = 'legalScreen',
  UtilityConnectService = 'utilityConnectService',
  LmiZoneVerificationService = 'lmiZoneVerificationService',
  DocusignService = 'docusignService',
  OtherAttributes = 'otherAttributes',
  EsignService = 'esignService',
  PaymentScreen = 'paymentScreen',
  PaymentService = 'paymentService',
}

/**
 * Screens whose lifecycle record owns contract-link interaction attributes.
 * Passed explicitly by the rendering screen — inferring it from planId /
 * utilityAccountId silently mis-routed clicks to userScreen. (SUBEXP-3193)
 */
export type ContractLinkScreen = EventName.LegalScreen | EventName.UserScreen;

export enum Status {
  Rendered = 'rendered',
  Completed = 'completed',
  Unknown = 'unknown',
  UCTokenFetched = 'utility_connect_token_fetched',
  Started = 'started',
  Failed = 'failed',
  Timeout = 'timeout',
  Redirected = 'redirected',
  Error = 'error',
  Signed = 'signed',
  Declined = 'declined',
  Expired = 'expired',
  UrlFetched = 'url_fetched',
}

class LoggerSingleton {
  private static instance: LoggerSingleton;

  private _screenLifecycleData: Record<string, any>;

  private _leadIdRecoveryAttempted: boolean;

  private constructor() {
    this._screenLifecycleData = {};
    this._leadIdRecoveryAttempted = false;
  }

  public static getInstance(): LoggerSingleton {
    if (!LoggerSingleton.instance) {
      LoggerSingleton.instance = new LoggerSingleton();
    }
    return LoggerSingleton.instance;
  }

  public async logEvent(eventData: EventData = { status: 'unknown' }, eventName: string) {
    let signupLeadId = store.get('SIGNUP_FUNNEL_LEAD_ID');
    const needsScreenData = Object.keys(this._screenLifecycleData).length === 0;
    // create-user is the only place that sets SIGNUP_FUNNEL_LEAD_ID, and
    // handle-resuming-user wipes the store, so a resuming user has no lead id and
    // every screen they reach after that is silently never persisted. Recover it
    // from the user's lead here once per page load, since a user without a lead
    // has nothing to recover.
    const needsLeadId = signupLeadId === undefined || signupLeadId === null;

    if (needsScreenData || (needsLeadId && !this._leadIdRecoveryAttempted)) {
      const userId = store.get('USER_ID');
      if (userId) {
        const leadData = await getSignupFunnelLead({ userId });
        if (needsLeadId) this._leadIdRecoveryAttempted = true;
        if (leadData !== undefined && leadData !== null && typeof leadData === 'object') {
          if (needsLeadId && leadData.id !== undefined && leadData.id !== null) {
            signupLeadId = leadData.id;
            store.set('SIGNUP_FUNNEL_LEAD_ID', signupLeadId);
          }
          if (needsScreenData) {
            this._screenLifecycleData = {
              ...leadData,
              otherAttributes: {
                ...leadData.otherAttributes,
                path: new Set<string>(leadData.otherAttributes?.path || []),
                totalDuration: leadData.otherAttributes?.totalDuration || 0,
              },
            };
          }
        }
      }
    }

    if (!this._screenLifecycleData[eventName]) {
      this._screenLifecycleData[eventName] = {
        status: 'unknown',
        endTime: 0,
        startTime: 0,
        duration: 0,
      };
    }

    if (this._screenLifecycleData.otherAttributes === undefined) {
      this._screenLifecycleData.otherAttributes = {
        path: new Set<string>(['utilityScreen']),
        totalDuration: 0,
      };
    }

    // A completed screen must never be walked back to rendered. Only screen-mount
    // effects report Rendered, so a later one is always a re-entry (re-mount, a
    // contract-preview click) and not real progress. Started is deliberately not
    // guarded — it marks service events that can legitimately retry. (SUBEXP-3193)
    const isBackwardsToRendered =
      this._screenLifecycleData[eventName].status === Status.Completed &&
      eventData.status === Status.Rendered;
    const effectiveStatus = isBackwardsToRendered ? undefined : eventData.status;

    this._screenLifecycleData[eventName] = mergeEventData(
      this._screenLifecycleData[eventName],
      // Drop only the status; the rest of the payload is still a legitimate append.
      isBackwardsToRendered ? omit(eventData, 'status') : eventData,
    );

    // Only advance lifecycle timers when this call explicitly sets status
    if (effectiveStatus === Status.Completed) {
      this._screenLifecycleData[eventName].endTime = new Date().getTime();

      // Duration must only be counted once — on Completed — otherwise later
      // status-less appends (preview/proofDocUpload/etc.) re-add the same duration.
      const { endTime, startTime } = this._screenLifecycleData[eventName];
      if (endTime !== 0 && startTime !== 0) {
        const duration = endTime - startTime;
        this._screenLifecycleData[eventName].duration = duration;
        this._screenLifecycleData.otherAttributes.totalDuration += duration;
      }
    } else if (effectiveStatus === Status.Rendered || effectiveStatus === Status.Started) {
      const { path } = this._screenLifecycleData.otherAttributes;
      this._screenLifecycleData.otherAttributes = {
        path: path instanceof Set ? path.add(eventName) : [],
        totalDuration: this._screenLifecycleData.otherAttributes.totalDuration || 0,
      };
      this._screenLifecycleData[eventName].startTime = new Date().getTime();
      this._screenLifecycleData[eventName].endTime = 0;
    }
    if (signupLeadId !== undefined && signupLeadId !== null) {
      const params = {
        id: Number(signupLeadId),
        data: {
          [eventName]: this._screenLifecycleData[eventName],
          otherAttributes: {
            ...this._screenLifecycleData.otherAttributes,
            path: Array.from(this._screenLifecycleData.otherAttributes.path),
          },
        },
      };
      try {
        await updateSignUpFunnelLead(params);
      } catch (error) {
        const loggerError = new Error();
        loggerError.name = 'LoggerError';
        loggerError.message = `Failed to log event for screen: ${eventName}`;
        loggerError.stack = error instanceof Error ? error.stack : undefined;
        loggerError.cause = error.message || 'Unknown error';
        // Our own analytics beacon failed to send, not a user-facing bug. (SUBEXP-2883)
        trackError(loggerError, {
          level: 'warning',
          extras: {
            event: 'logEvent',
            message: `Failed to log event for screen: ${eventName}`,
            data: this._screenLifecycleData,
          },
        });
      }
    }
  }
}

export const Logger = LoggerSingleton.getInstance();
